<template>
  <div class="pa-ovl" role="dialog" aria-modal="true" aria-labelledby="pa-contact-title" @click.self="close">
    <div class="pa-cmodal">
      <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">support_agent</i></div>
      <h3 id="pa-contact-title" class="pa-cmodal__title">Message your SEO Manager</h3>
      <p class="pa-cmodal__body">{{ body }}</p>
      <textarea
        ref="text"
        v-model="text"
        class="pa-cmodal__text"
        :maxlength="maxLength"
        placeholder="Type your question or requirements"
        :disabled="busy"
      ></textarea>
      <p v-if="error" class="pa-err pa-cmodal__err">{{ error }}</p>
      <div class="pa-cmodal__acts">
        <button type="button" class="pa-btn pa-btn--out" :disabled="busy" @click="close">Cancel</button>
        <button type="button" class="pa-btn pa-btn--orange" :disabled="busy || !text.trim()" @click="send">
          <span v-if="busy" class="pa-spin pa-spin--light"></span>
          Send message
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { showPopAuthorityToast } from '@/utils/popAuthority'

/**
 * "Message your SEO Manager" box (design pa-app.jsx `contactCM` + Modal): the client's message is
 * emailed to the site's campaign manager with the client as Reply-To
 * (POST /api/pop-authority/sites/<id>/contact-manager/). Emits `close` and, after sending, `sent`.
 */
export default {
  name: 'PopAuthorityContactManagerModal',
  props: {
    siteId: { type: Number, required: true },
    /** What the message is about, shown in the box and in the email subject. */
    topic: { type: String, default: '' },
    managerName: { type: String, default: '' },
    managerEmail: { type: String, default: '' },
  },
  data () {
    return {
      text: '',
      busy: false,
      error: '',
      maxLength: 4000,
    }
  },
  computed: {
    body () {
      const who = [this.managerName, this.managerEmail].filter(Boolean).join(', ') || 'Your SEO Manager'
      return `${who}. Replies within 1 business day.${this.topic ? ` Topic: ${this.topic}` : ''}`
    },
  },
  mounted () {
    document.addEventListener('keydown', this.onKeydown)
    this.$nextTick(() => {
      if (this.$refs.text) this.$refs.text.focus()
    })
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    onKeydown (event) {
      if (event.key === 'Escape' && !this.busy) this.close()
    },
    close () {
      if (this.busy) return
      this.$emit('close')
    },
    async send () {
      const message = this.text.trim()
      if (!message || this.busy) return
      this.busy = true
      this.error = ''
      try {
        const response = await this.$store.dispatch('sendPopAuthorityManagerMessage', {
          siteId: this.siteId,
          body: { topic: this.topic, message },
        })
        const data = response && response.data
        if (!(data && data.status === 'SUCCESS')) {
          this.error = (data && data.msg) || 'Could not send your message. Please try again.'
          return
        }
        // design pa-app.jsx contactCM toast: "Message sent to Sarah. Expect a reply within 1 business day"
        const name = String(data.managerName || this.managerName || '').trim().split(' ')[0] || 'your SEO Manager'
        showPopAuthorityToast(this, `Message sent to ${name}. Expect a reply within 1 business day`)
        this.$emit('sent', data)
        this.$emit('close')
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.msg
        this.error = msg || 'Could not send your message. Please try again.'
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup/setup.scss';
@import './setup/designModal.scss';

.pa-spin--light {
  border-color: rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
}
</style>
